import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import {useState, useEffect, useContext} from 'react';
import styled from 'styled-components';
import {useNavigate} from 'react-router-dom';
import {EmailLinkform} from './EmailLinkForm';
import {EmailPasswordForm} from './EmailPasswordForm';
import {AlternateAuth} from './AlternateAuth';
import {LoginProvider, LoginContext, LoginScreen} from './LoginContext';
import {EmailSentNotice} from './EmailSentNotice';
import {
  getRedirectResult,
  isSignInWithEmailLink,
  signInWithEmailLink,
  sendEmailVerification,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
} from 'firebase/auth';
import {SignInButtonFlat} from './SignInButton';
import {OrSeparator} from './OrSeparator';
import Swal from 'sweetalert2';
import {StanfordLogo as StanfordLogoBase} from 'branding/StanfordLogo';
import {Loading} from 'components/loading/Loading';
import {useCourseId} from 'hooks/router/useUrlParams';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import {useCourseTheme} from 'course/contexts/ThemeContext';
import {useSetPageTitle} from 'contexts/accessibility/AccessibilityContext';
import {AUTH_TEST_IDS} from './authTestIds';
import {carryReferralCode} from '@cip/core/application/referrals';
import {getSignInErrorMessage} from './signInErrors';

export const Login = () => {
  const [showSignIn, setShowSignIn] = useState(true);
  const setPageTitle = useSetPageTitle();
  useEffect(() => {
    setPageTitle('Sign In');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, []);
  const forgotPasswordText =
    'Enter the email address associated with your Code in Place account. We will send you further instructions for resetting your password.';
  const emailSignInText =
    'Enter the email address associated with your Code in Place account. We will send you a link to sign in.';
  // Emailed links often open in a different browser from the one the visitor
  // started in (e.g. out of an Instagram in-app browser), so the URL is the only
  // way a referral code survives signing in.
  const url = carryReferralCode({
    link: window.location.origin + window.location.pathname,
    search: window.location.search,
  });
  const urlWithoutSignInLinkParams = () =>
    carryReferralCode({
      link: location.href.split('?')[0],
      search: location.search,
    });
  const auth = firebase.auth();
  const user = auth.currentUser;
  const navigate = useNavigate();
  const actionCodeSettings = {
    url: url,
    handleCodeInApp: true,
  };
  const t = useTranslateFunction('miscUI');

  const showSignInError = (error: unknown) => {
    Swal.fire({
      icon: 'error',
      title: t('Oops...', {forceString: true}),
      text: t(getSignInErrorMessage(error), {forceString: true}),
    });
  };

  const showEmailAlert = async () => {
    try {
      const {value: email} = await Swal.fire({
        title: 'Input email address',
        input: 'email',
        inputLabel: 'Your email address',
        inputPlaceholder: 'Enter your email address',
      });
      await signInWithEmailLink(auth, email, window.location.href);
      window.localStorage.removeItem('emailForSignIn');
      history.pushState(null, '', urlWithoutSignInLinkParams());
    } catch (error) {
      showSignInError(error);
    }
  };

  useEffect(() => {
    // A failed Google redirect sign-in rejects only here, after the page
    // reloads; the click handler that started it is gone by then.
    getRedirectResult(auth).catch(showSignInError);
    if (user) {
      navigate(
        carryReferralCode({link: location.pathname, search: location.search}),
      );
    } else if (isSignInWithEmailLink(auth, window.location.href)) {
      // eslint-disable-next-line prefer-const -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      let email = window.localStorage.getItem('emailForSignIn');
      if (!email) {
        showEmailAlert();
      } else {
        signInWithEmailLink(auth, email, window.location.href)
           
          // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
          .then(result => {
            window.localStorage.removeItem('emailForSignIn');
            history.pushState(null, '', urlWithoutSignInLinkParams());
          })
          .catch(showSignInError);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, []);

  const handleEmailLinkSignIn = async (
    auth,
    magicLinkEmail,
    actionCodeSettings,
  ) => {
    await sendSignInLinkToEmail(auth, magicLinkEmail, actionCodeSettings);
    window.localStorage.setItem('emailForSignIn', magicLinkEmail);
  };

  // eslint-disable-next-line @eslint-react/no-nested-component-definitions -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const InitialLanding = () => {
    const [, setLoginState] = useContext(LoginContext);
    const courseId = useCourseId();
    const t = useTranslateFunction('miscUI');
    // TODO:  see github issue # 966
    const isCS105 = courseId === 'cs105f24';
    return (
      <div className="d-flex flex-column mt-3">
        <AlternateAuth auth={auth} />
        {!isCS105 && <OrSeparator />}
        {!isCS105 && (
          <SignInButtonFlat
            className="mb-2 text-white"
            variant="secondary"
            data-testid={AUTH_TEST_IDS.createAccountLandingButton}
            onClick={() => {
              setShowSignIn(false);
              setLoginState(LoginScreen.LOGIN_FORM);
            }}
          >
            {t('Create an Account')}
          </SignInButtonFlat>
        )}
        {!isCS105 && (
          <SignInButtonFlat
            className="mb-2 text-white"
            variant="secondary"
            data-testid={AUTH_TEST_IDS.existingAccountLandingButton}
            onClick={() => {
              setShowSignIn(true);
              setLoginState(LoginScreen.LOGIN_FORM);
            }}
          >
            {t('I Have an Account')}
          </SignInButtonFlat>
        )}
      </div>
    );
  };

  // eslint-disable-next-line @eslint-react/no-nested-component-definitions -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const LoginForm = () => {
    return (
      <div>
        <div className="d-flex flex-column">
          <EmailPasswordForm auth={auth} showSignIn={showSignIn} />
          <OrSeparator />
          <AlternateAuth auth={auth} btnVariant="outline-secondary" />
        </div>
        <div style={{maxWidth: '500px'}} className="text-center">
          <SwitchText>
            {showSignIn
              ? t('New to Code In Place?')
              : t('Already have an account?')}
          </SwitchText>
          <SignInButtonFlat
            style={{width: '100%'}}
            variant="light"
            data-testid={AUTH_TEST_IDS.modeSwitchButton}
            onClick={() => setShowSignIn(!showSignIn)}
          >
            {showSignIn ? t('Create Account') : t('Sign In')}
          </SignInButtonFlat>
        </div>
      </div>
    );
  };

  // eslint-disable-next-line @eslint-react/no-nested-component-definitions -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const LoginBody = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    const [loginState, setLoginState] = useContext(LoginContext);

    switch (loginState) {
      case LoginScreen.INIT_LANDING:
        return <InitialLanding />;
      case LoginScreen.UNVERIFIED:
        return (
          <EmailSentNotice
            emailHandler={sendEmailVerification}
            handlerArgs={[user, actionCodeSettings]}
          />
        );
      case LoginScreen.LOGIN_FORM:
        return (
          <div>
            <div className="mt-3">
              <LoginForm />
            </div>
          </div>
        );
      case LoginScreen.FORGOT_PASWORD:
        return (
          <EmailLinkform
            auth={auth}
            emailHandler={sendPasswordResetEmail}
            actionCodeSettings={actionCodeSettings}
            title={'Reset Password'}
            body={forgotPasswordText}
          />
        );

      case LoginScreen.EMAIL_LINK:
        return (
          <EmailLinkform
            auth={auth}
            emailHandler={handleEmailLinkSignIn}
            actionCodeSettings={actionCodeSettings}
            title={'One-time link'}
            body={emailSignInText}
          />
        );
    }
  };

  const {loginPageImageUrl, loginPageTitle, isLoading} = useCourseTheme();
  if (isLoading) {
    return <Loading />;
  }
  return (
    <OuterWrapper>
      {location.href.includes('apiKey') ? (
        <Loading />
      ) : (
        <LoginWrapper className="d-flex flex-column align-items-center">
          {!!loginPageImageUrl ? (
            <CourseLogo
              src={loginPageImageUrl}
              alt={`${loginPageTitle} ${t('Logo')}`}
            />
          ) : (
            <StanfordLogo
              alt="Stanford Logo"
            />
          )}
          <h1>{loginPageTitle ?? t('Code in Place')}</h1>
          <LoginProvider>
            <LoginBodyOuter>
              <LoginBody />
            </LoginBodyOuter>
          </LoginProvider>
        </LoginWrapper>
      )}
    </OuterWrapper>
  );
};

export const GenericBrandedWrapper = ({children}) => {
  const t = useTranslateFunction('miscUI');
  return (
    <OuterWrapper>
      <LoginWrapper className="d-flex flex-column align-items-center">
        <StanfordLogo
          alt="Stanford Logo"
        />
        <h1>{t('Code in Place')}</h1>
        {children}
      </LoginWrapper>
    </OuterWrapper>
  );
};

const LoginWrapper = styled.div`
  width: 100%;
  max-width: 300px;
  margin-bottom: 40px;
`;

const OuterWrapper = styled.div`
  display: flex;
  justify-content: center;
  height: 100vh;
`;

const StanfordLogo = styled(StanfordLogoBase)`
  background-size: contain;
  background-repeat: no-repeat;
  height: 100px;
  flex-grow: 1;
  max-height: 300px;
`;

const CourseLogo = styled.img`
  background-size: contain;
  background-repeat: no-repeat;
  object-fit: contain;
  flex-grow: 1;
  max-height: 250px;
  max-width: 400px;
  width: 100%;
`;

const LoginBodyOuter = styled.div``;

const SwitchText = styled.div`
  text-align: center;
  margin-top: 20px;
`;
